import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Row, Col, Panel, InputGroup, Glyphicon } from 'react-bootstrap';
import { DragSource, DropTarget } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend'
import _ from 'lodash';
import classNames from 'classnames';
import TextInput from './TextInput';
import CheckBox from './CheckBox';
import Select from './Select';
import Button from './Button';
import { DYNAMIC_FIELD_TYPES } from './DynamicFields';
import AutoComplete from './AutoComplete';
import { getObjectValue } from '../../helpers/utils';
import { defaultClientType } from '../../helpers/config';
import { privateFormFieldColor, sectionFormFieldColor, errorBackgroundColor, colorMap } from './../../helpers/colors';
import { fieldSource,
         fieldTarget,
         collectSource,
         collectTarget } from '../../../client/dnd.js';


// Drag sources and drop targets only interact
// if they have the same string type.
export const FORM_FIELD = 'FORM_FIELD';

const DEFAULT_ROWS = 3;

export const DEFAULT_FIELD = {
    props: {
        required: false,
        label: '',
    },
    type: 'TextInput',
    publicHidden: false,
    name: '',
    visibility: ['ALL'],
    '$Editing$': false,
    '$Name$': '',
    '$Id$': ''
};

const URL_TARGETS = [{label: 'New window', value: '_blank'},
                     {label: 'Current page', value: '_self'}];

export const RESERVED_FIELD_PLACEHOLDERS = [
    'Name', 'Referral', 'FirstName', 'LastName', 'MiddleName', 'Email', 'Phone', 'PublicIntakeDate', 'Signature', 'SignatureDateTime',
    'PublicIntakeSourceHost', 'PublicIntakeSourceFullUrl', 'dynamic', 'Signed', 'Paid', 'Fee', 'FeePaid', 'FeeOwed', 'FeeUnpaid',
    'id', 'owner_id', 'parent_id', 'body', 'deleted_at', 'created_at', 'updated_at', 'OwnerId',
    'Owner', 'true', 'false', 'undefined', 'CaseType', 'ClientType', 'ContactType', 'AssignedTo'
];

class FormEditorField extends PureComponent {
    constructor(props) {
        super(props);
        this.onChange = this.onChange.bind(this);
        this.preventValueArrayChange = false;
        this.addValueArray = this.addValueArray.bind(this);
        this.onEditValueArray = this.onEditValueArray.bind(this);
        this.deleteValueArray = this.deleteValueArray.bind(this);
        this.valueRenderer = this.valueRenderer.bind(this);
        this.optionRenderer = this.optionRenderer.bind(this);
        this.getFieldLabel = this.getFieldLabel.bind(this);
        const types = DYNAMIC_FIELD_TYPES.filter(type => type.name !== 'Hidden' && type.name !== 'CaseType' && (!props.disallowedFieldTypes || !props.disallowedFieldTypes.includes(type.name)));
        this.state = {
            field: DEFAULT_FIELD,
            types,
            advancedOpened: false,
            hasFocus: false,
        };
    }

    componentDidMount() {
        this.componentWillReceiveProps(this.props);
        // Use empty image as a drag preview so browsers don't draw it
        // and we can draw whatever we want on the custom drag layer instead.
        this.props.connectDragPreview(getEmptyImage(), {
            // IE fallback: specify that we'd rather screenshot the node
            // when it already knows it's being dragged so we can hide it with CSS.
            captureDraggingState: true,
        });

    }

    componentWillReceiveProps(nextProps) {
        let field = {...nextProps.initialField};
        if (field.type === 'Choice') {
            if (!field.props['options']) {
                field.props['options'] = [""];
            }
        }

        this.setState({field});
    }

    addValueArray(index, entryName) {
        let field = _.cloneDeep(this.state.field);
        let values = getObjectValue(field, entryName);
        values.splice(index + 1, 0, "");

        // Changing the state causes render to rerender components,
        // causing onEditValueArray to fire. Disable such updates until the state has
        // been successfully modified.
        this.preventValueArrayChange = true;
        this.setState({field}, () => {
            this.preventValueArrayChange = false;
        });

        this.props.onChange(field);

    }

    onEditValueArray(index, entryName, value) {
        if (this.preventValueArrayChange) {
            return;
        }

        let field = _.cloneDeep(this.state.field);
        let values = getObjectValue(field, entryName);
        values[index] = value;
        this.setState({field});
        this.props.onChange(field);
    }

    deleteValueArray(index, entryName) {
        let field = _.cloneDeep(this.state.field);
        let values = getObjectValue(field, entryName);
        values.splice(index, 1);
        if (values.length == 0) {
            values.push("");
        }

        this.preventValueArrayChange = true;
        this.setState({field}, () => {
            this.preventValueArrayChange = false;
        });

        this.props.onChange(field);
    }

    onChange(evt) {
        let fieldName = evt.target.name;
        let value = evt.target.value;
        let field = _.cloneDeep(this.state.field);
        switch (fieldName) {
        case 'type':
            field[fieldName] = this.state.types[evt.target.selectedIndex].name;
            switch (field[fieldName]) {
            case 'TextArea':
                field.props['rows'] = DEFAULT_ROWS;
                break;

            case 'URL':
                field.props['url'] = 'http://';
                field.props['target'] = '_blank';
                break;

            case 'Choice':
                field.props['options'] = [""];
                field.props['customEnabled'] = true;
                break;

            case 'Contact':
                if (!field.props['clientType'] && this.props.currentConfig.client && this.props.currentConfig.client.length > 0) {
                    field.props['clientType'] = this.props.currentConfig.client[0].type;
                }

                if (!field.props['clientLabel']) {
                    field.props['clientLabel'] = '{FirstName}';
                    if (field.props['clientType'] === 'Client') {
                        field.props['clientLabel'] += ' {MiddleName} {LastName}';
                    }
                }

                break;

            default:
                break;
            }

            break;

        case 'publicHidden':
            field['publicHidden'] = evt.target.checked;
            break;

        case 'required':
        case 'customEnabled':
        case 'clientNewAllowed':
            field.props[fieldName] = evt.target.checked;
            break;

        case 'name':
            // PLACEHOLDER REGEX
            field['$Name$'] = value.replace(/[^a-zA-Z0-9]/g, '');
            break;

        case 'rows':
            field.props[fieldName] = value.replace(/\D/g,'');
            break;

        case 'target':
            field.props[fieldName] = URL_TARGETS[evt.target.selectedIndex].value;
            break;

        case 'visibility':
            field['visibility'] = value.map(v => v.value);
            break;

        case 'label':
            if (_.isString(value)) {
                field.props['label'] = value;
            } else {
                field = {
                    ...field,
                    ...value,
                    '$Editing$': false,
                    '$Id$': field['$Id$'],
                    '$Name$': value.name.replace(new RegExp('$' + this.props.prefix.replace(/\./g, '\.')), ''),
                };
            }

            break;

        default:
            field.props[fieldName] = value;
            break;
        }

        this.setState({field})
        this.props.onChange(field);
    }

    valueRenderer(f, isDragging, showName=true) {
        let field = f;
        if (field.value) {
            field = field.value;
        }

        return (<div style={{ color: field.publicHidden && privateFormFieldColor, opacity: isDragging ? 0.5 : 1 }}>{this.getFieldLabel(field, showName)}</div>);
    }

    optionRenderer(f) {
        return this.valueRenderer(f, false, true);
    }

    getFieldLabel(field, showName) {
        if (_.isString(field)) {
            return field;
        }

        return `${field.props.label}${field.props.required ? ' *' : ''}${showName ? ` (${field.name})` : ''}`;
    }

    render() {
        const {
            isDragging,
            connectDragSource,
            connectDropTarget,
            disabled = false,
            visibility = false,
            suggestedFields = []
        } = this.props;

        let editingDisabled = disabled;
        let showRequired = true;
        let showPublic = true;
        let showName = true;
        let showHelp = true;
        let field = this.state.field;
        let contactOptions = [];
        switch (field.type) {
        case 'URL':
            showRequired = false;
            break;

        case 'Separator':
            showRequired = false;
            showHelp = false;
            showName = false;
            break;

        case 'Contact':
            if (this.props.currentConfig && this.props.currentConfig.client) {
                contactOptions = this.props.currentConfig.client.map(value => {
                    return {
                        label: value.type,
                        value: value.type
                    };
                });
            }

            break;
        }

        showPublic = showPublic && (!this.props.options || this.props.options.public === undefined || this.props.options.public);
        let cds = connectDragSource;
        if (disabled) {
            cds = (x) => x;
        }

        const collapseFields = (toggle = false) => {
            const f = { ...field };
            f['$Editing$'] = toggle ? !f['$Editing$'] : true;
            this.setState({
                hasFocus: f['$Editing$'],
                field: f
            });

            this.props.collapseFields(f);
        }

        let backgroundColor;
        if (field.type === 'Separator') {
            backgroundColor = sectionFormFieldColor;
        }

        if (field.error) {
            backgroundColor = errorBackgroundColor;
        }

        const labelSuggestions = suggestedFields.map(f => { return {
            label: this.getFieldLabel(f, true),
            value: f,
        }});

        return connectDropTarget(cds(
            <div style={{opacity: isDragging ? 0.5 : 1, cursor: 'grab', marginTop: -15}}>
            <Row className={classNames({'has-error': field.error})}>
                <Col xs={12} style={{backgroundColor, height: 42, cursor: 'grab'}}>
                <InputGroup>
                    <InputGroup.Addon>
                    {field.error ?
                        <Glyphicon glyph="remove" style={{marginBottom: 15}} />
                        :
                        <Glyphicon glyph="sort" style={{marginBottom: 15}} />
                    }
                    </InputGroup.Addon>
                    <AutoComplete
                        field={{
                            name: 'label',
                            error: field.error ? field.error['label'] : undefined,
                            touched: field.touched,
                            value: {
                                label: this.getFieldLabel(field, false),
                                value: field
                            },
                            onChange: (obj) => this.onChange({ target: { name: 'label', value: obj ? obj.value : '' }})
                        }}
                        allowNew
                        returnSelectedObject
                        disabled={editingDisabled}
                        options={labelSuggestions}
                        style={{paddingTop: 3}}
                        valueRenderer={field => this.valueRenderer(field, isDragging, false)}
                        optionRenderer={this.optionRenderer}
                        shouldKeyDownEventCreateNewOption={evt => {
                            // Allow ','. Original react-select forces new value creation normally on comma.
                            const keyCode = evt.keyCode;
                            switch (keyCode) {
                                case 9: // TAB
                                case 13: // ENTER
                                    return true;
                                default:
                                    return false;
                            }
                        }}
                    />
                    <InputGroup.Button>
                        <Button onClick={(evt) => {
                                    evt.preventDefault();
                                    collapseFields(true);
                                }}
                                disabled={editingDisabled}
                                icon={field['$Editing$'] ? 'triangle-bottom' : 'triangle-right'}
                                bsStyle="link"
                                style={{marginTop: -10}}
                                tooltip="More"
                                tooltipPosition="left"
                        />
                        <Button onClick={() => {
                                    this.props.onDelete(field);
                                }}
                                disabled={editingDisabled || (RESERVED_FIELD_PLACEHOLDERS.includes(field['$Name$']) && !field['$New$'])}
                                icon="trash"
                                iconStyle={{color: `rgba(${colorMap['red']}, 1)`, marginLeft: -7}}
                                bsStyle="link"
                                style={{marginTop: -10, marginRight: -5, width: 15}}
                                width={15}
                                tooltip="Delete"
                                tooltipPosition="left"
                        />
                    </InputGroup.Button>
                </InputGroup>
                </Col>
            </Row>
            {field['$Editing$'] && !isDragging && (
            <Panel style={{ marginLeft: 3, marginRight: 3, backgroundColor: '#F5F5F5' }}>
                <Row>
                    <Col xs={12}>
                        <Select field={{
                                    name: 'type',
                                    error: field.error ? field.error['type'] : undefined,
                                    touched: field.touched,
                                    defaultValue: this.state.types[0].label,
                                    value: this.state.types.find(type => {
                                        return field.type == type.name;
                                    }).label,
                                    onChange: (evt) => {this.onChange(evt)}
                                }}
                                label='Field Type'
                                disabled={['Name', 'FirstName', 'Email', 'Phone'].includes(field.name)}
                                name='type'
                                options={this.state.types.map(type => {
                                    return type.label
                                })}
                                onChange={this.onChange}/>
                    </Col>
                </Row>
                {field.type === 'Contact' && (
                    <Row>
                        <Col xs={12}>
                            <Select field={{
                                        name: 'clientType',
                                        error: field.error ? field.error['clientType'] : undefined,
                                        touched: field.touched,
                                        defaultValue: field.props.clientType,
                                        value: field.props.clientType,
                                        onChange: (evt) => {this.onChange(evt)}
                                    }}
                                    label='Contact Type'
                                    name='clientType'
                                    options={contactOptions}
                                    onChange={this.onChange}/>
                        </Col>
                    </Row>
                )}
                <Row>
                {showRequired && (
                    <Col xs={12} md={6}>
                        <CheckBox field={{
                                      name: 'required',
                                      error: field.error ? field.error['required'] : undefined,
                                      touched: field.touched,
                                  }}
                                  label='Required *'
                                  help='If checked, will make the field mandatory.'
                                  name='required'
                                  checked={field.props && field.props.required}
                                  onChange={this.onChange}/>
                    </Col>)}
                {showPublic && (
                    <Col xs={12} md={6}>
                        <CheckBox field={{
                                      name: 'publicHidden',
                                      error: field.error ? field.error['publicHidden'] : undefined,
                                      touched: field.touched,
                                  }}
                                  label={(<span style={{ color: privateFormFieldColor }}>Private</span>)}
                                  help='If checked, it will hide this field on public intake form.'
                                  name='publicHidden'
                                  checked={field.publicHidden}
                                  onChange={this.onChange}/>
                    </Col>)}
                </Row>
                {field.type === 'TextArea' && (
                <Row>
                    <Col xs={12}>
                        <TextInput field={{
                                       name: 'rows',
                                       error: field.error ? field.error['rows'] : undefined,
                                       touched: field.touched,
                                       defaultValue: field.props && field.props.rows,
                                       value: field.props && field.props.rows
                                   }}
                                   label='Number of Rows'
                                   name='rows'
                                   help='Number of rows to be diplayed.'
                                   onChange={this.onChange} />
                    </Col>
                </Row>)}
                {field.type === 'URL' && (
                <div>
                <Row>
                    <Col xs={12}>
                        <TextInput field={{
                                       name: 'url',
                                       error: field.error ? field.error['url'] : undefined,
                                       touched: field.touched,
                                       defaultValue: field.props && field.props.url,
                                       value: field.props && field.props.url
                                   }}
                                   label='URL'
                                   name='url'
                                   help='Specify link to be opened (eg: http://www.google.com). You can use templated placeholders.'
                                   onChange={this.onChange} />
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <Select field={{
                                    name: 'target',
                                    error: field.error ? field.error['target'] : undefined,
                                    touched: field.touched,
                                    defaultValue: URL_TARGETS[0].label,
                                    value: URL_TARGETS.find(target => {
                                        return field.props.target == target.value;
                                    }).label,
                                    onChange: (evt) => {this.onChange(evt)}
                                }}
                                label='URL Target'
                                name='target'
                                options={URL_TARGETS.map(target => {
                                    return target.label
                                })}
                                onChange={this.onChange}/>
                    </Col>
                </Row>
                </div>)}
                {field.type === 'Choice' && (
                    <div>
                    <Row>
                    <Col xs={12}>
                        <CheckBox field={{
                                      name: 'customEnabled',
                                      error: field.error ? field.error['customEnabled'] : undefined,
                                      touched: field.touched,
                                  }}
                                  label='Allow custom choice'
                                  help='If checked, it will allow to enter value other then the predefined choices.'
                                  name='customEnabled'
                                  checked={field.props.customEnabled}
                                  onChange={this.onChange}/>
                    </Col>
                    </Row>
                    <Row>
                    <Col xs={12} style={{marginLeft: 15}}>
                        <h6>Choices</h6>
                        <hr/>
                    {field.props.options && field.props.options.map((option, index) => {
                        return (
                        <Row>
                        <Col xs={12}>
                            <InputGroup>
                            <TextInput field={{
                                          name: 'choiceOption' + index,
                                          error: undefined,
                                          touched: true,
                                          value: option,
                                          onChange: (evt) => {
                                               this.onEditValueArray(index, 'props.options', evt.target.value);
                                          }
                                      }}
                                      name={'choiceOption' + index}
                                      label={undefined}/>
                                <InputGroup.Button>
                                <Button onClick={() => this.deleteValueArray(index, 'props.options')}
                                        block
                                        icon="trash"
                                        tooltip="Add choice"/>
                                </InputGroup.Button>
                                <InputGroup.Button>
                                <Button onClick={() => this.addValueArray(index, 'props.options', "")}
                                        block
                                        icon="plus"
                                        tooltip="Remove choice"/>
                                </InputGroup.Button>
                            </InputGroup>
                        </Col>
                        </Row>);
                    })}
                    </Col>
                    </Row>
                    </div>

                )}
                {showName && (
                    <div>
                    <Row style={{marginLeft: 0}}>
                    <Col xs={12}>
                        <h6>Advanced&nbsp;
                            <Button bsStyle="link"
                                    onClick={() => this.setState({advancedOpened: !this.state.advancedOpened})}
                                    icon={this.state.advancedOpened ? "triangle-bottom" : "triangle-right"} />
                        </h6>
                    </Col>
                    </Row>
                    <Row>
                    <Col xs={12}>
                    <Panel  bsStyle="default"
                            collapsible
                            expanded={this.state.advancedOpened}>
                    <Row>
                        <Col xs={12}>
                            <TextInput field={{
                                            name: 'name',
                                            error: field.error ? field.error['name'] : undefined,
                                            touched: field.touched,
                                            defaultValue: field['$Name$'],
                                            value: field['$Name$']
                                       }}
                                       disabled={RESERVED_FIELD_PLACEHOLDERS.includes(field['$Name$']) && !field['$New$']}
                                       label='Placeholder Name'
                                       name='name'
                                       help='Placeholder name of the field. For use in templates (emails, documents, ...).'
                                       onChange={this.onChange}
                                       beforeAddon={'{' + this.props.prefix}
                                       afterAddon={'}'}/>
                        </Col>
                        {showHelp && (
                            <Col xs={12}>
                                <TextInput field={{
                                                name: 'help',
                                                error: field.error ? field.error['help'] : undefined,
                                                touched: field.touched,
                                                defaultValue: field.props.help,
                                                value: field.props.help
                                            }}
                                            label='Description'
                                            name='help'
                                            help='Help description for given field.'
                                            onChange={this.onChange} />
                            </Col>)}
                        {visibility && (
                            <Col xs={12}>
                            <AutoComplete field={{
                                            name: 'visibility',
                                            error: field.error ? field.error['visibility'] : undefined,
                                            touched: field.touched,
                                            value: field['visibility'],
                                            onChange: (value) => {
                                                let foundAllIndexOld = -1;
                                                if (field['visibility']) {
                                                    foundAllIndexOld = field['visibility'].findIndex(v => v.value === 'ALL');
                                                }

                                                let foundAllIndex = value.findIndex(v => v.value === 'ALL');
                                                if (foundAllIndexOld >= 0 && foundAllIndex < 0) {
                                                    value.splice(foundAllIndex, 1);
                                                } else if ((foundAllIndexOld < 0 && foundAllIndex >= 0) ||
                                                           value.length === visibility.length) {
                                                    value = [{label: 'ALL', value: 'ALL'}];
                                                }

                                                this.onChange({
                                                    target: {
                                                        name: 'visibility',
                                                        value,
                                                    },
                                                });
                                            }}}
                                          multiselect
                                          returnSelectedObject
                                          label="Visibility"
                                          options={[{label: 'ALL', value: 'ALL'}, ...visibility]}
                            />
                            </Col>
                        )}
                    </Row>
                    {field.type === 'Contact' && (
                        <div>
                        <Row>
                        <Col xs={12}>
                            <TextInput field={{
                                            name: 'clientLabel',
                                            error: field.error ? field.error['clientLabel'] : undefined,
                                            touched: field.touched,
                                            defaultValue: field.props.clientLabel,
                                            value: field.props.clientLabel
                                        }}
                                        label='Display Contact As'
                                        name='clientLabel'
                                        help='Use template placeholders for given contact type to specify how the contact should be displayed in the list (eg: {FirstName}).'
                                        onChange={this.onChange} />
                        </Col>
                        </Row>
                        <Row>
                        <Col xs={12}>
                            <CheckBox field={{
                                        name: 'clientNewAllowed',
                                        error: field.error ? field.error['clientNewAllowed'] : undefined,
                                        touched: field.touched,
                                    }}
                                    label='Allow New'
                                    help='If checked, it will allow to enter value other then the predefined choices, except when publicly shared.'
                                    name='clientNewAllowed'
                                    checked={field.props.clientNewAllowed && field.props.clientType !== defaultClientType}
                                    disabled={field.props.clientType === defaultClientType}
                                    onChange={this.onChange}
                            />
                        </Col>
                        </Row>
                        </div>
                    )}
                    </Panel>
                    </Col>
                    </Row>
                    </div>
                    )}
                    </Panel>)}
                    {field['$Editing$'] && !isDragging && (
                    <div style={{marginTop: -22, marginBottom: 10, textAlign: 'center'}}>
                    <Button onClick={() => {
                                this.props.onAdd(field);
                            }}
                            disabled={editingDisabled}
                            icon="plus"
                            bsStyle="link"
                            label="Add Field"
                            iconStyle={{paddingBottom: '5px'}}
                            tooltip="Add new field"
                            tooltipPosition="left"
                    />
                    </div>
                    )}
            </div>
        ));
    }
}

const mapStateToProps = (state, ownProps) => {
    return {
        currentConfig: state.user.config
    }
};

FormEditorField.propTypes = {
    onDelete: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onAdd: PropTypes.func.isRequired,
    initialField: PropTypes.object.isRequired,
    prefix: PropTypes.string,
    suggestedFields: PropTypes.array,

    moveField: PropTypes.func.isRequired,
    findField: PropTypes.func.isRequired,
    collapseFields: PropTypes.func.isRequired,
    options: PropTypes.object,
    disable: PropTypes.bool,
    visibility: PropTypes.array,
    // React DnD:
    id: PropTypes.string.isRequired,
    isDragging: PropTypes.bool.isRequired,
    connectDragSource: PropTypes.func.isRequired,
    connectDropTarget: PropTypes.func.isRequired,
    connectDragPreview: PropTypes.func.isRequired
}

export default _.flow(
  connect(mapStateToProps),
  DragSource(FORM_FIELD, fieldSource, collectSource),
  DropTarget(FORM_FIELD, fieldTarget, collectTarget)
)(FormEditorField);
